<template>
  <section id="about" class="about">
    <div class="wrap">
      <div class="head-row">
        <p class="kicker mono"><span class="idx">02</span> — About</p>
        <p class="est mono">Est. 2015 · Board-level specialists</p>
      </div>

      <h2 class="display" ref="head">
        <span class="ln"><span>The repair bench you can <em>trust.</em></span></span>
      </h2>

      <div class="body">
        <div class="lead">
          <p class="big">Twelve years of fixing what others gave up on.</p>
          <p class="sub">NEXXT GEN COMPUTERS keeps homes and businesses running with
            fast, honest repair. From micro-soldering and diagnostics to screen
            swaps, data recovery and upgrades — we fix it right the first time,
            and tell you straight when we can't.</p>
          <a href="#contact" class="cta">
            <span>Talk to a technician</span>
            <span class="arw">→</span>
          </a>

          <div class="chip-wrap" :class="{ off: !powered }">
            <button class="power" :class="{ on: powered }" @click="togglePower"
              :aria-pressed="powered" :aria-label="powered ? 'Power off' : 'Power on'">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M12 3 v9"/><path d="M6.5 7 a7 7 0 1 0 11 0"/>
              </svg>
              <span class="mono">{{ powered ? 'ON' : 'OFF' }}</span>
            </button>
            <svg class="chip" aria-hidden="true" viewBox="-140 0 620 342" fill="none" stroke-linecap="round">
              <!-- circuit traces -->
              <g class="traces" stroke="var(--coral)" stroke-width="1.5">
                <path d="M150 108 V40 M150 40 h40" pathLength="1"/>
                <path d="M192 150 H272 M272 150 v-46 h-30" pathLength="1"/>
                <path d="M150 192 V262 M150 262 h-46" pathLength="1"/>
                <path d="M108 150 H30 M30 150 v50 h34" pathLength="1"/>
                <path d="M192 120 h58 M250 120 v-40" pathLength="1"/>
                <path d="M120 192 v54 M120 246 h44" pathLength="1"/>
              </g>
              <!-- trace nodes -->
              <g class="nodes" fill="var(--coral)">
                <circle cx="190" cy="40" r="3"/><circle cx="242" cy="104" r="3"/>
                <circle cx="104" cy="262" r="3"/><circle cx="64" cy="200" r="3"/>
                <circle cx="250" cy="80" r="3"/><circle cx="164" cy="246" r="3"/>
              </g>
              <!-- chip pins -->
              <g class="pins" stroke="#3a3a42" stroke-width="2">
                <line x1="126" y1="108" x2="126" y2="92"/><line x1="150" y1="108" x2="150" y2="92"/><line x1="174" y1="108" x2="174" y2="92"/>
                <line x1="126" y1="192" x2="126" y2="208"/><line x1="150" y1="192" x2="150" y2="208"/><line x1="174" y1="192" x2="174" y2="208"/>
                <line x1="108" y1="126" x2="92" y2="126"/><line x1="108" y1="150" x2="92" y2="150"/><line x1="108" y1="174" x2="92" y2="174"/>
                <line x1="192" y1="126" x2="208" y2="126"/><line x1="192" y1="150" x2="208" y2="150"/><line x1="192" y1="174" x2="208" y2="174"/>
              </g>
              <!-- data packets travelling the traces -->
              <g class="packets" fill="#ffd9cf">
                <circle r="2.6"><animateMotion dur="2.6s" repeatCount="indefinite" path="M150 108 V40 h40"/></circle>
                <circle r="2.6"><animateMotion dur="2.9s" repeatCount="indefinite" begin="0.4s" path="M192 150 H272 v-46 h-30"/></circle>
                <circle r="2.6"><animateMotion dur="2.4s" repeatCount="indefinite" begin="0.8s" path="M150 192 V262 h-46"/></circle>
                <circle r="2.6"><animateMotion dur="3.1s" repeatCount="indefinite" begin="0.2s" path="M108 150 H30 v50 h34"/></circle>
                <circle r="2.6"><animateMotion dur="2.7s" repeatCount="indefinite" begin="1s" path="M192 120 h58 v-40"/></circle>
                <circle r="2.6"><animateMotion dur="2.5s" repeatCount="indefinite" begin="0.6s" path="M120 192 v54 h44"/></circle>
              </g>

              <!-- chip body -->
              <rect class="body" x="108" y="108" width="84" height="84" rx="8" stroke="#4a4a54" stroke-width="2"/>
              <rect class="core" x="128" y="128" width="44" height="44" rx="4" stroke="var(--coral)" stroke-width="1.5"/>
              <!-- internal die circuitry -->
              <g class="die" stroke="var(--coral)" stroke-width="1" stroke-linecap="round">
                <path d="M128 142 h10 v-8 M138 158 h-10 M172 142 h-10 v10 M162 166 h10"/>
                <path d="M150 128 v12 h-8 M150 172 v-12 h8"/>
                <rect x="143" y="143" width="14" height="14" rx="2" stroke-width="1.3"/>
                <line x1="150" y1="143" x2="150" y2="139"/>
                <line x1="157" y1="150" x2="161" y2="150"/>
              </g>


              <!-- connector: chip → fan -->
              <path class="link" d="M208 150 H340" stroke="var(--coral)" stroke-width="1.5" pathLength="1"/>
              <circle class="pkt" r="2.6" fill="#ffd9cf">
                <animateMotion dur="1.8s" repeatCount="indefinite" path="M208 150 H340"/>
              </circle>

              <!-- cooling fan -->
              <g class="fan-wrap">
                <rect class="fan-frame" x="340" y="90" width="120" height="120" rx="12"
                  stroke="#4a4a54" stroke-width="2"/>
                <circle cx="400" cy="150" r="52" stroke="#3a3a42" stroke-width="1.5"/>
                <g class="fan-blades" :style="{ animationDuration: fanSpeed + 's' }"
                  stroke="var(--coral)" stroke-width="2" fill="rgba(242,107,78,0.06)">
                  <path d="M400 150 Q378 118 400 104 Q416 126 400 150 Z"/>
                  <path d="M400 150 Q432 128 446 150 Q424 166 400 150 Z"/>
                  <path d="M400 150 Q422 182 400 196 Q384 174 400 150 Z"/>
                  <path d="M400 150 Q368 172 354 150 Q376 134 400 150 Z"/>
                </g>
                <circle class="fan-hub" cx="400" cy="150" r="9" fill="#0f0f13" stroke="var(--coral)" stroke-width="2"/>
                <circle cx="349" cy="99" r="2.5" fill="#3a3a42"/><circle cx="451" cy="99" r="2.5" fill="#3a3a42"/>
                <circle cx="349" cy="201" r="2.5" fill="#3a3a42"/><circle cx="451" cy="201" r="2.5" fill="#3a3a42"/>
              </g>

              <!-- CPU temperature + fan RPM readout -->
              <g class="temp">
                <rect x="240" y="24" width="132" height="58" rx="8" fill="#0f0f13" stroke="#2c2c32" stroke-width="1"/>
                <text class="temp-lbl" x="254" y="42">CPU TEMP</text>
                <text class="temp-val" x="254" y="58">{{ powered ? temp.toFixed(0) + '°C' : '--' }}</text>
                <rect x="330" y="34" width="26" height="24" rx="3" fill="none" stroke="#2c2c32" stroke-width="1"/>
                <rect class="temp-bar" x="333" :y="55 - tempFill" width="20" :height="tempFill" rx="2" :fill="tempColor"/>
                <line x1="254" y1="66" x2="358" y2="66" stroke="#2c2c32" stroke-width="1"/>
                <text class="rpm" x="254" y="78">FAN {{ rpm }} RPM</text>
              </g>

              <!-- RAM module -->
              <g class="ram">
                <path class="ram-link" d="M150 192 V250 H340" stroke="var(--coral)" stroke-width="1.5" pathLength="1"/>
                <circle class="pkt" r="2.4" fill="#ffd9cf">
                  <animateMotion dur="2.2s" repeatCount="indefinite" path="M150 192 V250 H340"/>
                </circle>
                <rect class="ram-body" x="340" y="234" width="120" height="32" rx="4" fill="#0f0f13" stroke="#4a4a54" stroke-width="1.5"/>
                <g class="ram-leds" fill="var(--coral)">
                  <rect x="350" y="240" width="6" height="20" rx="1"/>
                  <rect x="362" y="240" width="6" height="20" rx="1"/>
                  <rect x="374" y="240" width="6" height="20" rx="1"/>
                  <rect x="386" y="240" width="6" height="20" rx="1"/>
                  <rect x="398" y="240" width="6" height="20" rx="1"/>
                  <rect x="410" y="240" width="6" height="20" rx="1"/>
                </g>
                <text class="ram-lbl" x="424" y="253">DDR</text>
                <line x1="360" y1="266" x2="360" y2="272" stroke="#3a3a42" stroke-width="2"/>
                <line x1="440" y1="266" x2="440" y2="272" stroke="#3a3a42" stroke-width="2"/>
              </g>

              <!-- SSD / NVMe storage -->
              <g class="ssd">
                <path class="ssd-link" d="M120 192 V310 H60 V330 M120 310 H340" stroke="var(--coral)" stroke-width="1.5" pathLength="1"/>
                <circle class="pkt" r="2.4" fill="#ffd9cf">
                  <animateMotion dur="1.6s" repeatCount="indefinite" path="M120 192 V310 H340"/>
                </circle>
                <rect class="ssd-body" x="340" y="294" width="120" height="34" rx="4" fill="#0f0f13" stroke="#4a4a54" stroke-width="1.5"/>
                <text class="ssd-lbl" x="350" y="308">NVMe SSD</text>
                <text class="ssd-rate" x="450" y="308" text-anchor="end">{{ ssdRate }} MB/s</text>
                <rect x="350" y="314" width="100" height="6" rx="3" fill="rgba(255,255,255,0.06)"/>
                <rect class="ssd-fill" x="350" y="314" :width="ssdBar" height="6" rx="3" fill="var(--coral)"/>
              </g>

              <!-- SMPS / power supply (feeds the chip) -->
              <g class="smps">
                <path class="smps-link" d="M-24 150 H30" stroke="var(--coral)" stroke-width="1.5" pathLength="1"/>
                <circle class="pkt" r="2.4" fill="#ffd9cf">
                  <animateMotion dur="1.4s" repeatCount="indefinite" path="M-96 150 H30"/>
                </circle>
                <rect class="smps-body" x="-96" y="112" width="72" height="76" rx="6" fill="#0f0f13" stroke="#4a4a54" stroke-width="1.5"/>
                <text class="smps-lbl" x="-60" y="132" text-anchor="middle">SMPS</text>
                <g class="smps-rails" stroke="var(--coral)" stroke-width="1">
                  <line x1="-84" y1="158" x2="-36" y2="158"/>
                  <line x1="-84" y1="170" x2="-36" y2="170"/>
                </g>
                <text class="smps-v" x="-60" y="150" text-anchor="middle">12V · 5V · 3.3V</text>
              </g>

              <!-- network / RJ45 port -->
              <g class="net">
                <path class="net-link" d="M120 192 V310 H60 M-40 246 H60 V310" stroke="var(--coral)" stroke-width="1.5" pathLength="1" opacity="0.9"/>
                <path class="net-link" d="M-40 246 H-96" stroke="var(--coral)" stroke-width="1.5" pathLength="1"/>
                <circle class="pkt" r="2.4" fill="#ffd9cf">
                  <animateMotion dur="1.8s" repeatCount="indefinite" path="M-96 246 H60 V310 H120 V192"/>
                </circle>
                <rect class="net-body" x="-136" y="222" width="40" height="48" rx="4" fill="#0f0f13" stroke="#4a4a54" stroke-width="1.5"/>
                <path class="net-jack" d="M-128 236 h24 v14 h-6 v6 h-12 v-6 h-6 z" stroke="#5a5a64" stroke-width="1.2"/>
                <circle class="net-led" cx="-116" cy="230" r="2.5" fill="var(--coral)"/>
                <text class="net-lbl" x="-116" y="282" text-anchor="middle">LAN</text>
              </g>
            </svg>
          </div>
        </div>

        <ol class="ledger" ref="ledger">
          <li v-for="(s, i) in services" :key="s" class="row">
            <span class="rule"></span>
            <span class="fill"></span>
            <span class="n mono">{{ String(i + 1).padStart(2, '0') }}</span>
            <span class="svc display">{{ s }}</span>
            <span class="arw" aria-hidden="true">↗</span>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>

<script>
import { maskReveal, gsap } from '@/composables/useReveal'

export default {
  name: 'AboutSection',
  data() {
    return {
      services: ['Board-level repair', 'Data recovery', 'Screen & display', 'Upgrades & tune-ups'],
      powered: localStorage.getItem('ng-chip-power') !== 'off', // default ON
      temp: 42,
      ssd: 480
    }
  },
  computed: {
    tempFill() { return Math.round(((this.temp - 35) / 45) * 22) }, // 35–80°C → 0–22px
    tempColor() { return this.temp > 65 ? '#F26B4E' : this.temp > 52 ? '#e0a24a' : '#5fb98f' },
    // hotter CPU → faster fan (shorter duration) and higher RPM
    fanSpeed() { return this.powered ? (2.4 - ((this.temp - 38) / 34) * 1.7).toFixed(2) : 0 },
    rpm() { return this.powered ? Math.round((1200 + ((this.temp - 38) / 34) * 3000) / 10) * 10 : 0 },
    ssdRate() { return this.powered ? Math.round(this.ssd) : 0 },
    ssdBar() { return this.powered ? Math.round((this.ssd / 1000) * 100) : 0 } // 0–1000 MB/s → 0–100px
  },
  mounted() {
    maskReveal(this.$refs.head.querySelectorAll('.ln span'), { trigger: this.$el, start: 'top 80%' })
    this._tempTimer = setInterval(() => {
      if (!this.powered) return
      const next = this.temp + (Math.random() - 0.45) * 6
      this.temp = Math.min(72, Math.max(38, next))
      this.ssd = Math.min(980, Math.max(120, this.ssd + (Math.random() - 0.5) * 420))
    }, 1400)
    gsap.from(this.$refs.ledger.querySelectorAll('.row'), {
      yPercent: 60, opacity: 0, duration: 0.8, ease: 'power4.out', stagger: 0.09,
      scrollTrigger: { trigger: this.$refs.ledger, start: 'top 85%' }
    })
    gsap.from(this.$refs.ledger.querySelectorAll('.rule'), {
      scaleX: 0, transformOrigin: 'left', duration: 1, ease: 'power3.out', stagger: 0.09,
      scrollTrigger: { trigger: this.$refs.ledger, start: 'top 85%' }
    })
  },
  beforeUnmount() { clearInterval(this._tempTimer) },
  methods: {
    togglePower() {
      this.powered = !this.powered
      localStorage.setItem('ng-chip-power', this.powered ? 'on' : 'off')
      if (this.powered) this.temp = 42
    }
  }
}
</script>

<style scoped>
.about { background: var(--ink); color: var(--text-on-ink); padding: clamp(44px, 5vw, 80px) 0; }
.wrap { width: min(1360px, 94vw); margin: 0 auto; }

.head-row { display: flex; justify-content: space-between; align-items: baseline;
  border-bottom: 1px solid #2c2c32; padding-bottom: 18px; margin-bottom: clamp(34px, 4vw, 60px); }
.kicker { color: var(--text-on-ink); font-size: 12px; letter-spacing: 3px; text-transform: uppercase; margin: 0; }
.kicker .idx { color: var(--coral); }
.est { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: #6a6a70; margin: 0; }

.display { font-family: var(--font-display); font-weight: 700; letter-spacing: -2.5px; line-height: 1.02;
  font-size: clamp(28px, 5vw, 72px); margin: 0 0 clamp(44px, 5vw, 76px); text-align: left; white-space: nowrap; }
.display em { font-style: normal; color: var(--coral); }
.ln { display: block; overflow: hidden; }

.body { display: grid; grid-template-columns: 1.05fr 1fr; gap: clamp(34px, 6vw, 96px);
  align-items: start; }
.big { font-family: var(--font-display); font-weight: 500; font-size: clamp(22px, 2.4vw, 34px);
  line-height: 1.22; letter-spacing: -0.8px; margin: 0 0 24px; max-width: 16ch; }
.sub { font-size: clamp(15px, 1.4vw, 18px); line-height: 1.68; color: #a6a6ad; max-width: 52ch; margin: 0 0 32px; }

.cta { display: inline-flex; align-items: center; gap: 12px; text-decoration: none;
  color: var(--text-on-ink); font-family: var(--font-mono); font-size: 13px; letter-spacing: 2px;
  text-transform: uppercase; padding-bottom: 5px; border-bottom: 1px solid var(--text-on-ink); }
.cta .arw { transition: transform 0.35s var(--ease); }
.cta:hover { color: var(--coral); border-color: var(--coral); }
.cta:hover .arw { transform: translateX(6px); }

/* animated chip / PCB motif */
.chip-wrap { position: relative; margin-top: clamp(24px, 3vw, 40px); max-width: 520px;
  filter: drop-shadow(0 0 24px rgba(242,107,78,0.14)); transition: filter 0.5s ease; }
.chip { width: 100%; height: auto; display: block; }

/* power button */
.power { position: absolute; top: -6px; right: 0; z-index: 3;
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  padding: 7px 14px; border-radius: 999px; background: transparent;
  border: 1px solid #33333a; color: #6a6a70; font-size: 11px; letter-spacing: 2px;
  transition: color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease; }
.power:hover { border-color: #55555f; }
.power.on { color: var(--coral); border-color: rgba(242,107,78,0.5);
  box-shadow: 0 0 14px rgba(242,107,78,0.2); }

/* powered-off state: kill glow + drain colour + freeze motion */
.chip-wrap.off { filter: none; }
/* recolour strokes only (never add fill to stroke-only paths → avoids triangles) */
.chip-wrap.off .chip .traces path,
.chip-wrap.off .chip .die,
.chip-wrap.off .chip .link,
.chip-wrap.off .chip .ram-link,
.chip-wrap.off .chip .ssd-link,
.chip-wrap.off .chip .ssd-fill,
.chip-wrap.off .chip .smps-link,
.chip-wrap.off .chip .smps-rails line,
.chip-wrap.off .chip .net-link,
.chip-wrap.off .chip .core,
.chip-wrap.off .chip .orbit { stroke: #4a4a52 !important; }
.chip-wrap.off .chip .ssd-fill,
.chip-wrap.off .chip .net-led { fill: #3a3a42 !important; }
.chip-wrap.off .chip .ssd-rate,
.chip-wrap.off .chip .smps-v { fill: #55555f !important; }
.chip-wrap.off .chip .smps-rails line, .chip-wrap.off .chip .net-led { animation: none !important; }
/* filled coral bits (nodes, fan blades, hub, ram leds) → grey */
.chip-wrap.off .chip .nodes circle,
.chip-wrap.off .chip .fan-blades path,
.chip-wrap.off .chip .fan-hub,
.chip-wrap.off .chip .ram-leds rect { fill: #3a3a42 !important; }
.chip-wrap.off .chip .fan-blades path,
.chip-wrap.off .chip .fan-hub { stroke: #4a4a52 !important; }
.chip-wrap.off .chip .temp-val { fill: #55555f !important; }
/* hide moving packets (SMIL doesn't stop via CSS) */
.chip-wrap.off .chip .packets, .chip-wrap.off .chip .pkt { display: none !important; }
/* freeze CSS animations */
.chip-wrap.off .chip .fan-blades, .chip-wrap.off .chip .orbit,
.chip-wrap.off .chip .ram-leds rect, .chip-wrap.off .chip .die,
.chip-wrap.off .chip .link, .chip-wrap.off .chip .ram-link,
.chip-wrap.off .chip .traces path,
.chip-wrap.off .chip .core { animation: none !important; }
.chip-wrap.off .chip .ram-leds rect { opacity: 0.35; }

/* traces draw in, then energy pulses along them */
.chip .traces path { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 1.4s ease forwards, energy 3s ease-in-out infinite 1.6s; }
.chip .traces path:nth-child(2) { animation-delay: 0.15s, 1.9s; }
.chip .traces path:nth-child(3) { animation-delay: 0.3s, 2.1s; }
.chip .traces path:nth-child(4) { animation-delay: 0.45s, 1.7s; }
.chip .traces path:nth-child(5) { animation-delay: 0.6s, 2.3s; }
.chip .traces path:nth-child(6) { animation-delay: 0.75s, 2s; }

.chip .nodes circle { transform-origin: center; transform-box: fill-box;
  animation: nodePulse 2.2s ease-in-out infinite; }
.chip .nodes circle:nth-child(even) { animation-delay: 1.1s; }

.chip .core { transform-origin: 150px 150px; animation: coreGlow 2.4s ease-in-out infinite; }
.chip .body { opacity: 0; animation: fadeIn 0.6s ease 0.4s forwards; }
.chip .pins line { opacity: 0; animation: fadeIn 0.5s ease 0.8s forwards; }
.chip .die { fill: none; opacity: 0; animation: fadeIn 0.6s ease 1.2s forwards, energy 3s ease-in-out infinite 1.4s; }
.chip .packets circle { filter: drop-shadow(0 0 4px var(--coral)); }
.chip .orbit { transform-origin: 150px 150px; opacity: 0.6;
  animation: spin 6s linear infinite, fadeIn 0.6s ease 1s forwards; }

/* connector + fan */
.chip .link { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 1s ease 1.4s forwards, energy 3s ease-in-out infinite 2.4s; }
.chip .pkt { opacity: 0; animation: fadeIn 0.4s ease 2.2s forwards; }
.chip .fan-frame, .chip .fan-wrap > circle, .chip .fan-hub { opacity: 0;
  animation: fadeIn 0.6s ease 1.8s forwards; }
.chip .fan-blades { transform-origin: 400px 150px;
  animation: spin 1.4s linear infinite; animation-delay: 2s; }

/* temperature readout */
.chip .temp { opacity: 0; animation: fadeIn 0.6s ease 2.4s forwards; }
.chip .temp-lbl { fill: #6a6a70; font-family: var(--font-mono); font-size: 8px; letter-spacing: 1.5px; }
.chip .temp-val { fill: var(--coral); font-family: var(--font-mono); font-size: 14px; font-weight: 700; }
.chip .temp-bar { transition: height 0.9s var(--ease), y 0.9s var(--ease), fill 0.9s ease; }

/* fan RPM label (inside temp badge) */
.chip .rpm { fill: #8a8a92; font-family: var(--font-mono); font-size: 9px; letter-spacing: 1px; }

/* RAM module */
.chip .ram-link { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 1s ease 1.6s forwards, energy 3s ease-in-out infinite 2.6s; }
.chip .ram-body, .chip .ram-lbl { opacity: 0; animation: fadeIn 0.6s ease 2.2s forwards; }
.chip .ram-lbl { fill: #6a6a70; font-family: var(--font-mono); font-size: 9px; letter-spacing: 1px; }
.chip .ram-leds rect { opacity: 0.25; animation: led 1.2s ease-in-out infinite; }
.chip .ram-leds rect:nth-child(1) { animation-delay: 0s; }
.chip .ram-leds rect:nth-child(2) { animation-delay: 0.15s; }
.chip .ram-leds rect:nth-child(3) { animation-delay: 0.3s; }
.chip .ram-leds rect:nth-child(4) { animation-delay: 0.45s; }
.chip .ram-leds rect:nth-child(5) { animation-delay: 0.6s; }
.chip .ram-leds rect:nth-child(6) { animation-delay: 0.75s; }
@keyframes led { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }

/* SSD module */
.chip .ssd-link { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 1s ease 1.8s forwards, energy 3s ease-in-out infinite 2.8s; }
.chip .ssd-body, .chip .ssd-lbl, .chip .ssd-rate { opacity: 0; animation: fadeIn 0.6s ease 2.4s forwards; }
.chip .ssd-lbl { fill: #8a8a92; font-family: var(--font-mono); font-size: 9px; letter-spacing: 1px; }
.chip .ssd-rate { fill: var(--coral); font-family: var(--font-mono); font-size: 10px; font-weight: 700; }
.chip .ssd-fill { transition: width 0.9s var(--ease); }

/* SMPS / power supply */
.chip .smps-link { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 0.9s ease 0.3s forwards, energy 3s ease-in-out infinite 1.4s; }
.chip .smps-body, .chip .smps-lbl, .chip .smps-v, .chip .smps-rails { opacity: 0; animation: fadeIn 0.6s ease 0.6s forwards; }
.chip .smps-lbl { fill: #8a8a92; font-family: var(--font-mono); font-size: 9px; letter-spacing: 1px; }
.chip .smps-v { fill: var(--coral); font-family: var(--font-mono); font-size: 6.5px; letter-spacing: 0.5px; }
.chip .smps-rails line { animation: energy 2.4s ease-in-out infinite; }
.chip .smps-rails line:nth-child(2) { animation-delay: 0.4s; }
.chip .smps-rails line:nth-child(3) { animation-delay: 0.8s; }

/* network / RJ45 */
.chip .net-link { stroke-dasharray: 1; stroke-dashoffset: 1;
  animation: draw 1s ease 1.4s forwards, energy 3s ease-in-out infinite 2.4s; }
.chip .net-body, .chip .net-jack, .chip .net-lbl { opacity: 0; animation: fadeIn 0.6s ease 1.8s forwards; }
.chip .net-lbl { fill: #8a8a92; font-family: var(--font-mono); font-size: 9px; letter-spacing: 1px; }
.chip .net-led { animation: led 0.9s ease-in-out infinite 2s; }

@keyframes draw { to { stroke-dashoffset: 0; } }
@keyframes energy { 0%,100% { stroke: #4a3530; } 50% { stroke: var(--coral); } }
@keyframes nodePulse { 0%,100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.6); } }
@keyframes coreGlow { 0%,100% { opacity: 0.6; } 50% { opacity: 1; filter: drop-shadow(0 0 6px var(--coral)); } }
@keyframes fadeIn { to { opacity: 1; } }
@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .chip .traces path, .chip .nodes circle, .chip .core, .chip .body,
  .chip .pins line, .chip .die, .chip .orbit, .chip .link,
  .chip .fan-frame, .chip .fan-wrap > circle, .chip .fan-hub, .chip .fan-blades, .chip .temp,
  .chip .rpm, .chip .ram-link, .chip .ram-body, .chip .ram-lbl, .chip .ram-leds rect,
  .chip .ssd-link, .chip .ssd-body, .chip .ssd-lbl, .chip .ssd-rate,
  .chip .smps-link, .chip .smps-body, .chip .smps-lbl, .chip .smps-v, .chip .smps-rails line,
  .chip .net-link, .chip .net-body, .chip .net-jack, .chip .net-lbl, .chip .net-led {
    animation: none; opacity: 1; stroke-dashoffset: 0; }
  .chip .packets, .chip .pkt { display: none; }
}

.ledger { list-style: none; margin: 0; padding: 0; }
.row { position: relative; display: grid; grid-template-columns: 46px 1fr auto; align-items: center;
  column-gap: 18px; padding: clamp(16px, 1.8vw, 26px) clamp(10px, 1.4vw, 20px);
  cursor: default; overflow: hidden; }
.row .rule { position: absolute; left: 0; top: 0; height: 1px; width: 100%; background: #2c2c32; }
.row:last-child::after { content: ''; position: absolute; left: 0; bottom: 0; height: 1px; width: 100%; background: #2c2c32; }
/* coral wipe that fills from the left on hover */
.row .fill { position: absolute; inset: 0; background: var(--coral); transform: scaleX(0);
  transform-origin: left; transition: transform 0.5s var(--ease); z-index: 0; }
.row .n, .row .svc, .row .arw { position: relative; z-index: 1;
  transition: color 0.45s var(--ease), transform 0.45s var(--ease); }
.row .n { color: var(--coral); font-size: 12px; letter-spacing: 1px; }
.row .svc { font-family: var(--font-display); font-weight: 600; letter-spacing: -0.8px;
  font-size: clamp(22px, 2.6vw, 36px); }
.row .arw { color: #6a6a70; font-size: clamp(18px, 1.6vw, 24px); opacity: 0; transform: translate(-8px, 4px); }
.row:hover .fill { transform: scaleX(1); }
.row:hover .n { color: var(--ink); }
.row:hover .svc { color: var(--ink); transform: translateX(6px); }
.row:hover .arw { color: var(--ink); opacity: 1; transform: translate(0, 0); }

@media (max-width: 760px) {
  .body { grid-template-columns: 1fr; }
}
</style>
